import { create } from 'zustand';
import { AuthState } from '../types';
import { supabase } from '../services/supabase';

interface AuthStore extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  checkSession: () => Promise<boolean>;
  resendVerificationEmail: (email: string) => Promise<any>;
  checkEmailExists: (email: string) => Promise<boolean>; // Add this line
}

export const useAuthStore = create<AuthStore>((set) => ({ // Remove unused 'get'
  // ... existing state ...
  user: null,
  session: null,
  loading: true,

  // ... existing signIn ...
  signIn: async (email, password) => {
    set({ loading: true });
    try {
      // Make sure email and password are properly trimmed and validated
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        throw new Error('Email and password are required');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (error) throw error;

      set({
        user: data.user,
        session: data.session,
        loading: false,
      });
    } catch (error: any) {
      console.error('Sign in error:', error.message);
      set({ loading: false });
      throw error;
    }
  },

  // Sign up with email and password (RPC check removed)
  signUp: async (email, password) => {
    set({ loading: true });
    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        throw new Error('Email and password are required');
      }

      console.log('🔍 Starting final signup process for:', trimmedEmail);

      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network and try again.');
      }

      // *** Email existence check is now done BEFORE calling this function ***
      // Proceed directly with Supabase signup
      console.log(`🚀 Proceeding with supabase.auth.signUp for ${trimmedEmail}`);
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth-callback`,
          data: {
            signup_timestamp: new Date().toISOString()
          }
        }
      });

      // Handle potential errors during the actual signup process
      if (signUpError) {
        console.error('❌ Supabase signUp error:', signUpError);
        // Pass the original Supabase error message
        throw signUpError;
      }

      // Handle successful signup
      if (!data?.user) {
        console.error('❌ Signup returned no error but also no user data.');
        throw new Error('An unexpected error occurred during signup. Please try again.');
      }

      console.log('✅ Supabase signup successful, user needs verification:', data.user.email);

      // Store email in localStorage for the verification page
      localStorage.setItem('pendingVerificationEmail', trimmedEmail);

      // Update state
      set({
        user: data.user,
        session: data.session,
        loading: false,
      });

      // Return success data
      return {
        user: data.user,
        session: data.session,
        emailConfirmationRequired: !data.user?.email_confirmed_at
      };

    } catch (error: any) {
      console.error('❌ Sign up processing error (caught in store):', error.message);
      set({ loading: false });
      // Re-throw the error to be caught by the calling component
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    set({ loading: true });
    try {
      // First, clear all application stores to remove all user data
      // This ensures no data from previous user remains in memory
      
      // Import dynamically to avoid circular dependencies
      const { useUserStore } = await import('./userStore');
      const { useOptimizedUserStore } = await import('./optimizedUserStore');
      
      // Reset all store states to initial values
      useUserStore.setState({
        profile: null,
        vacationDays: [],
        dailyEntries: [],
        dailyTargets: [],
        calculations: null,
        isLoading: false,
        error: null
      });
      
      // Reset optimized store with caches
      useOptimizedUserStore.setState({
        profile: null,
        vacationDays: [],
        dailyEntries: [],
        dailyTargets: [],
        targetSummary: null,
        targetExceptions: [],
        calculations: null,
        error: null,
        profileLoading: false,
        vacationLoading: false,
        entriesLoading: false,
        targetsLoading: false,
        summaryLoading: false,
        exceptionsLoading: false,
        profileCache: null,
        vacationDaysCache: null,
        dailyEntriesCache: null,
        targetSummaryCache: null,
        exceptionsCache: null
      });
      
      // Then sign out from Supabase - using a try/catch to continue even if this fails
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.warn('Supabase signOut returned an error, but proceeding anyway:', error.message);
        }
      } catch (signOutError) {
        console.warn('Error during Supabase signOut, but proceeding anyway:', signOutError);
        // Continue with the sign out process despite the error
      }

      // Clear both localStorage and sessionStorage auth-related items
      try {
        // Clear potential auth data from both storage types
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.removeItem('supabase.auth.token');
      } catch (storageError) {
        console.warn('Error clearing storage during sign out:', storageError);
      }

      // Finally reset auth store state
      set({
        user: null,
        session: null,
        loading: false,
      });
      
      console.log('All user data cleared successfully');
    } catch (error: any) {
      console.error('Sign out error:', error.message);
      set({ loading: false });
      throw error;
    }
  },

  // Reset password
  resetPassword: async (email) => {
    set({ loading: true });
    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        throw new Error('Email is required');
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      set({ loading: false });
    } catch (error: any) {
      console.error('Reset password error:', error.message);
      set({ loading: false });
      throw error;
    }
  },

  // Check if user has an active session
  checkSession: async () => {
    set({ loading: true });
    
    try {
      const { data } = await supabase.auth.getSession();
      
      if (data?.session) {
        const { data: userData } = await supabase.auth.getUser();
        
        set({
          user: userData.user,
          session: data.session,
          loading: false,
        });
        return true;
      }
      
      set({
        user: null,
        session: null,
        loading: false,
      });
      return false;
    } catch (error) {
      console.error('Check session error:', error);
      set({
        user: null,
        session: null,
        loading: false,
      });
      return false;
    }
  },

  // Resend verification email
  resendVerificationEmail: async (email) => {
    set({ loading: true });
    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        throw new Error('Email is required');
      }
      
      console.log('🔄 Resending verification email to:', trimmedEmail);
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: trimmedEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth-callback`,
        }
      });
      
      if (error) {
        console.error('❌ Verification email resend error:', error.message);
        throw error;
      }
      
      console.log('✅ Verification email resent successfully');
      set({ loading: false });
      
      // Update localStorage with timestamp for rate limiting
      localStorage.setItem('lastVerificationEmailSent', new Date().toISOString());
      
      return { success: true };
    } catch (error: any) {
      console.error('❌ Verification email resend error:', error.message);
      set({ loading: false });
      throw error;
    }
  },

  // New function to check if email exists via RPC
  checkEmailExists: async (email: string): Promise<boolean> => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      // Or throw an error, depending on desired handling
      console.warn('Attempted to check empty email');
      return false; 
    }
    
    // Indicate loading state if needed, though maybe not for a quick check
    // set({ loading: true }); // Optional: depends on UX preference

    try {
      console.log(`📞 Calling RPC check_email_exists for ${trimmedEmail}`);
      const { data: emailExists, error: rpcError } = await supabase
        .rpc('check_email_exists', { p_email: trimmedEmail });

      if (rpcError) {
        console.error('❌ RPC check_email_exists error:', rpcError);
        // Throw a more user-friendly error or the original
        throw new Error('Could not verify email. Please try again later.');
      }

      console.log(`📬 RPC check_email_exists result: ${emailExists}`);
      // set({ loading: false }); // Match optional loading state set
      return emailExists === true;

    } catch (error) {
      console.error('❌ Error in checkEmailExists:', error);
      // set({ loading: false }); // Match optional loading state set
      // Re-throw the error so the UI component can handle it
      throw error;
    }
  },
}));

export default useAuthStore;